import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type TaxonomyField } from '@/entities/taxonomy';
import type { ResolvedList } from '@/entities/taxonomy-list';
import { type SchemaProblem } from '../../lib/validateSchema';
import { FieldHeaderCell } from './FieldHeaderCell';

interface SortableFieldHeaderCellProps {
  field: TaxonomyField;
  allFields: TaxonomyField[];
  /** For a linked field: the target schema's display name. */
  targetName?: string;
  isLast: boolean;
  accent: string;
  readOnly: boolean;
  onRename: (name: string) => void;
  onRenameEditingChange: (open: boolean) => void;
  onEdit: () => void;
  onConfigure: () => void;
  onReset: () => void;
  onLinkList: () => void;
  onUnlinkList: () => void;
  /** Set when this column is bound to a shared list. */
  boundList?: ResolvedList;
  /** Validate findings anchored to this whole column. */
  problems?: SchemaProblem[];
  onGenerateSample: () => void;
  onAddRight: () => void;
  onAddSubfield: () => void;
  onDetach: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

/** Column header cell made drag-sortable (dnd-kit). Dragging reorders columns;
 *  a top-level drag carries its subfield cluster (see FieldsEditor.reorderFields). */
export function SortableFieldHeaderCell({
  field,
  allFields,
  targetName,
  isLast,
  accent,
  readOnly,
  onRename,
  onRenameEditingChange,
  onEdit,
  onConfigure,
  onReset,
  onLinkList,
  onUnlinkList,
  boundList,
  problems,
  onGenerateSample,
  onAddRight,
  onAddSubfield,
  onDetach,
  onDuplicate,
  onRemove,
}: SortableFieldHeaderCellProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    disabled: readOnly,
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 30, position: 'relative' } : {}),
  };
  return (
    <th
      ref={setNodeRef}
      colSpan={2}
      scope="colgroup"
      style={style}
      className={`group/field overflow-hidden border-b border-l border-border-light px-2 py-2 text-left align-top ${
        field.parentId
          ? `border-t-2 border-dashed ${accent} bg-[color-mix(in_srgb,var(--surface)_50%,var(--card))]`
          : `border-t-2 ${accent} bg-surface`
      } ${isLast ? 'border-r' : ''} ${isDragging ? 'shadow-md' : ''}`}
    >
      <FieldHeaderCell
        field={field}
        allFields={allFields}
        targetName={targetName}
        readOnly={readOnly}
        isSubfield={!!field.parentId}
        dragHandleProps={readOnly ? undefined : { ...attributes, ...listeners }}
        onRename={onRename}
        onRenameEditingChange={onRenameEditingChange}
        onEdit={onEdit}
        onConfigure={onConfigure}
        onReset={onReset}
        onLinkList={onLinkList}
        onUnlinkList={onUnlinkList}
        boundList={boundList}
        problems={problems}
        onGenerateSample={onGenerateSample}
        onAddRight={onAddRight}
        onAddSubfield={onAddSubfield}
        onDetach={onDetach}
        onDuplicate={onDuplicate}
        onRemove={onRemove}
      />
    </th>
  );
}
